import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Check, Copy, ShieldCheck, ShieldOff } from 'lucide-react';
import { PageHeader } from '../../../components/layout/PageHeader';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { FormField, Input } from '../../../components/ui/FormField';
import { Skeleton } from '../../../components/ui/Skeleton';
import { useToast } from '../../../components/ui/Toast';
import { usePageTitle } from '../../../lib/hooks';
import {
  useMfaStatus,
  useEnableMfa,
  useVerifyMfa,
  useDisableMfa,
} from '../../../features/settings/network/mfa.api';

export const Route = createFileRoute('/_auth/settings/security')({
  component: SecuritySettingsPage,
});

const badgeEnabled =
  'text-xs font-semibold uppercase tracking-wide text-accent-700 bg-accent-50 border border-accent-200 rounded-full px-2 py-0.5';
const badgeDisabled =
  'text-xs font-semibold uppercase tracking-wide text-text-secondary bg-surface-2 border border-border rounded-full px-2 py-0.5';

function SecuritySettingsPage() {
  usePageTitle('Security');
  const { toast } = useToast();
  const status = useMfaStatus();
  const enableMfa = useEnableMfa();
  const verifyMfa = useVerifyMfa();
  const disableMfa = useDisableMfa();

  const [enrollment, setEnrollment] = useState<{ secret: string; otpauthUrl: string } | null>(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const enabled = status.data?.enabled ?? false;

  const messageOf = (err: unknown, fallback: string) =>
    err instanceof Error ? err.message : fallback;

  const handleEnable = async () => {
    setError(null);
    try {
      const result = await enableMfa.mutateAsync();
      setEnrollment({ secret: result.secret, otpauthUrl: result.otpauthUrl });
      setVerifyCode('');
      setCopied(false);
      // allow-silent-catch: rendered as an inline field message; no secret is issued on failure.
    } catch (err) {
      setError(messageOf(err, 'Could not start two-factor enrollment'));
    }
  };

  const handleVerify = async (event: React.FormEvent) => {
    event.preventDefault();
    if (verifyCode.length !== 6) return;
    setError(null);
    try {
      await verifyMfa.mutateAsync(verifyCode);
      setEnrollment(null);
      setVerifyCode('');
      toast({ type: 'success', title: 'Two-factor authentication enabled' });
      // allow-silent-catch: rendered as an inline field message; MFA stays inactive on failure.
    } catch (err) {
      setError(messageOf(err, 'That code did not verify'));
      setVerifyCode('');
    }
  };

  const handleDisable = async (event: React.FormEvent) => {
    event.preventDefault();
    if (disableCode.length !== 6) return;
    setError(null);
    try {
      await disableMfa.mutateAsync(disableCode);
      setDisableCode('');
      toast({ type: 'success', title: 'Two-factor authentication disabled' });
      // allow-silent-catch: rendered as an inline field message; MFA stays enabled on failure.
    } catch (err) {
      setError(messageOf(err, 'That code did not verify'));
      setDisableCode('');
    }
  };

  const handleCopy = async () => {
    if (!enrollment) return;
    await navigator.clipboard.writeText(enrollment.secret);
    setCopied(true);
  };

  return (
    <div className="max-w-2xl">
      <PageHeader
        title="Two-factor authentication"
        subtitle="A 6-digit authenticator code protects sign-in and sensitive actions such as payouts, vault movements and role changes."
      />

      <div className="flex flex-col gap-4">
        <Card className="p-4 flex items-start justify-between gap-4 border border-border">
          <div className="flex items-start gap-3 min-w-0">
            {enabled ? (
              <ShieldCheck className="h-5 w-5 text-accent-600 shrink-0 mt-0.5" aria-hidden="true" />
            ) : (
              <ShieldOff className="h-5 w-5 text-text-muted shrink-0 mt-0.5" aria-hidden="true" />
            )}
            <div className="min-w-0">
              <p className="text-sm font-medium text-text-primary">
                Authenticator app (TOTP)
              </p>
              <p className="text-sm text-text-muted">
                {enabled
                  ? 'Codes from your authenticator app are required for sensitive actions.'
                  : 'Not set up yet. You will be asked for a code whenever you enable this.'}
              </p>
            </div>
          </div>
          {status.isLoading ? (
            <Skeleton className="h-6 w-20" />
          ) : (
            <span className={enabled ? badgeEnabled : badgeDisabled}>
              {enabled ? 'Enabled' : 'Off'}
            </span>
          )}
        </Card>

        {status.isError && (
          <p className="text-sm text-danger-text" role="alert">
            {messageOf(status.error, 'Could not load your two-factor status.')}
          </p>
        )}

        {!enabled && !enrollment && (
          <div className="flex flex-col gap-2">
            <Button
              type="button"
              variant="primary"
              loading={enableMfa.isPending}
              onClick={() => void handleEnable()}
            >
              Enable two-factor authentication
            </Button>
            {error && (
              <p className="text-sm text-danger-text" role="alert">
                {error}
              </p>
            )}
          </div>
        )}

        {enrollment && (
          <form onSubmit={(event) => void handleVerify(event)} className="flex flex-col gap-4">
            <Card className="p-4 flex flex-col gap-3 border border-border">
              <p className="text-sm text-text-muted">
                Add this key to your authenticator app (Google Authenticator, Authy, 1Password),
                then enter the 6-digit code it shows.
              </p>
              <div className="flex items-center gap-2">
                <code className="flex-1 min-w-0 rounded-md bg-surface-2 border border-border px-3 py-2 text-sm text-text-primary break-all">
                  {enrollment.secret}
                </code>
                <Button type="button" variant="secondary" size="sm" onClick={() => void handleCopy()}>
                  {copied ? <Check className="h-4 w-4" aria-hidden="true" /> : <Copy className="h-4 w-4" aria-hidden="true" />}
                  <span className="ml-1">{copied ? 'Copied' : 'Copy'}</span>
                </Button>
              </div>
            </Card>

            <FormField label="Verification code" required error={error ?? undefined} htmlFor="mfa-verify-code">
              <Input
                id="mfa-verify-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="000000"
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))}
                disabled={verifyMfa.isPending}
                error={!!error}
              />
            </FormField>

            <div className="flex items-center gap-2">
              <Button
                type="submit"
                variant="primary"
                loading={verifyMfa.isPending}
                disabled={verifyCode.length !== 6}
              >
                Confirm and enable
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setEnrollment(null);
                  setVerifyCode('');
                  setError(null);
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
        )}

        {enabled && (
          <form onSubmit={(event) => void handleDisable(event)} className="flex flex-col gap-4">
            <p className="text-sm text-text-muted">
              Turning this off removes the requirement for sensitive actions. Enter a current code
              to confirm.
            </p>
            <FormField label="Verification code" required error={error ?? undefined} htmlFor="mfa-disable-code">
              <Input
                id="mfa-disable-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="000000"
                value={disableCode}
                onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, ''))}
                disabled={disableMfa.isPending}
                error={!!error}
              />
            </FormField>
            <div>
              <Button
                type="submit"
                variant="danger"
                loading={disableMfa.isPending}
                disabled={disableCode.length !== 6}
              >
                Disable two-factor
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
